// Simple encryption/decryption utility
const ENCRYPTION_KEY = '8f@D#29rL!zM$wQe7m^X*Bp9Tk@Vc3Ln'; // Made key longer and more unique

export const encryptId = (id) => {
    try {
        // Convert id to string and ensure it's padded
        const idStr = id.toString().padStart(4, '0');

        // Add a simple checksum
        const checksum = id.toString().split('').reduce((a, b) => a + parseInt(b), 0);

        // Create the encoded string with checksum
        const encoded = `${checksum}${idStr}${Date.now().toString(36)}`;

        // XOR encryption with improved key handling
        const encrypted = encoded.split('').map((char, index) => {
            const keyChar = ENCRYPTION_KEY[index % ENCRYPTION_KEY.length];
            return String.fromCharCode(char.charCodeAt(0) ^ keyChar.charCodeAt(0));
        }).join('');

        // Convert to URL-safe base64
        return btoa(encrypted)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    } catch (error) {
        return null;
    }
};

export const decryptId = (encrypted) => {
    try {
        if (!encrypted) return null;

        // Restore base64 string
        const base64 = encrypted
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        // Decrypt the string
        const decoded = atob(base64);
        const decrypted = decoded.split('').map((char, index) => {
            const keyChar = ENCRYPTION_KEY[index % ENCRYPTION_KEY.length];
            return String.fromCharCode(char.charCodeAt(0) ^ keyChar.charCodeAt(0));
        }).join('');

        // Extract the ID (4 digits after checksum)
        const idMatch = decrypted.match(/^\d+(\d{4})/);
        if (!idMatch) return null;

        // Parse the ID and remove leading zeros
        const id = parseInt(idMatch[1], 10);

        // Verify checksum
        const originalChecksum = parseInt(decrypted[0]);
        const calculatedChecksum = id.toString().split('').reduce((a, b) => a + parseInt(b), 0);

        if (originalChecksum !== calculatedChecksum % 10) {
            return null;
        }

        return id;
    } catch (error) {
        return null;
    }
};



/**
 * Encrypts a value by serializing to JSON, XORing, and converting to URL-safe Base64.
 * @param {any} plainValue - The value to encrypt.
 * @returns {string|null} The encrypted string, or null on failure.
 */
export const encryptData = (plainValue) => {
    try {
        // 1. Serialize to JSON
        const json = JSON.stringify(plainValue);

        // 2. XOR Obfuscation
        let encrypted = "";
        for (let i = 0; i < json.length; i++) {
            const keyChar = ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
            // XOR the char code and append
            encrypted += String.fromCharCode(json.charCodeAt(i) ^ keyChar);
        }

        // 3. Convert to UTF-8 Bytes (matches C# Encoding.UTF8.GetBytes)
        const encoder = new TextEncoder();
        const utf8Bytes = encoder.encode(encrypted);

        // 4. Convert UTF-8 bytes to Base64
        // (btoa expects a binary string, so we convert the Uint8Array)
        let binaryString = "";
        for (let i = 0; i < utf8Bytes.length; i++) {
            binaryString += String.fromCharCode(utf8Bytes[i]);
        }
        const base64 = btoa(binaryString);

        // 5. Make URL Safe (Replace +, /, =)
        const safeBase64 = base64
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, "");

        return safeBase64;
    } catch (ex) {
        
        return null;
    }
};
/**
 * Decrypts a URL-safe Base64 string back to its original object.
 * @param {string} encryptedValue - The encrypted string.
 * @returns {any} The decrypted object, or null on failure.
 */
export const decryptData = (encryptedValue) => {
    try {
        if (!encryptedValue) return null;

        // 1. Restore Base64 format (Replace -, _)
        let base64 = encryptedValue.replace(/-/g, "+").replace(/_/g, "/");

        // 2. Restore Padding (=)
        while (base64.length % 4 !== 0) {
            base64 += "=";
        }

        // 3. Decode Base64 to UTF-8 Bytes
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        // 4. Decode UTF-8 to String (matches C# Encoding.UTF8.GetString)
        const decoder = new TextDecoder();
        const decoded = decoder.decode(bytes);

        // 5. XOR De-obfuscation
        let decrypted = "";
        for (let i = 0; i < decoded.length; i++) {
            const keyChar = ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
            decrypted += String.fromCharCode(decoded.charCodeAt(i) ^ keyChar);
        }

        // 6. Deserialize JSON
        return JSON.parse(decrypted);
    } catch (ex) {
        
        return null;
    }
};

